import ThemeBackground from "@pathwright/ui/src/components/background/ThemeBackground"
import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import Card from "@pathwright/ui/src/components/card/Card"
import CardBlock from "@pathwright/ui/src/components/card/CardBlock"
import CardLink from "@pathwright/ui/src/components/card/CardLink"
import FieldWrapper from "@pathwright/ui/src/components/form/form-utils/FieldWrapper"
import Link from "@pathwright/ui/src/components/link/Link"
import { media } from "@pathwright/ui/src/components/utils/styles"
import ColorPicker from "@pathwright/ux/src/components/color/ColorPicker"

import { ApolloCache } from "@apollo/client"
import { NativeSelect } from "@chakra-ui/react"
import Pathicon from "@pathwright/features/src/modules/pathicon/Pathicon"
import { Field, Form, Formik } from "formik"
import PropTypes from "prop-types"
import styled from "styled-components"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import {
  ThemeFragment,
  ThemeInput,
  ThemeType,
  useCreateSchoolThemeMutation,
  useUpdateSchoolThemeMutation
} from "@pathwright/graphql-client/src/generated"
import SchoolIcon from "../../lib/SchoolIcon"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { usePresetThemeFont } from "../../space/signup/SpaceSignupTheme"
import {
  PresetHeadingFontKey,
  formatPresetHeadingFontCss,
  parsePresetHeadingFontCss,
  presetHeadingFontCss,
  presetThemes
} from "../../theme/presets"

const StyledBackground = styled.div`
  position: relative;
  padding-top: 30px;
  padding-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  a {
    color: white;
    border-bottom: 1px solid white;
  }
  ${(media.max as any).phone`padding-top: 50px;`};
`

const StyledLink = styled(Link)`
  position: absolute;
  top: 10px;
  right: 10px;
`

const StyledIcon = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StyledFieldWrapper = styled(FieldWrapper)`
  .UIFieldWrapper__input {
    /* For more accurately aboslutely positioning font select input. */
    position: static !important;
  }
`

const BrandPreview = ({ cardPath }: { cardPath: string }) => (
  <StyledBackground>
    <ThemeBackground />
    <StyledLink to={`${cardPath}background/`}>Customize background</StyledLink>
    <StyledIcon>
      <SchoolIcon size={75} className={undefined} />
      <Link to={`${cardPath}icon/`}>Change icon & logo</Link>
    </StyledIcon>
  </StyledBackground>
)

// Loads the preset theme font.
// Currently doing so when user changes heading font in form,
// and, while we could do this only when they save the form,
// it doesn't seem all that necessary.
// This ensures that when the user saves the form, the newly selected
// heading font will be immediately reflected in the UIs using that font.
function PresetThemeFont({
  headingFont
}: {
  headingFont: PresetHeadingFontKey
}) {
  usePresetThemeFont(headingFont)
  return null
}

type ValidHeadingFont = PresetHeadingFontKey | null | undefined

// Use a "pretty" name for font, mainly resolving ugly "ProximaNova-Regular"
// which we likely should just use a ProximaNova variable font when we have
// time to update.
const PrettyHeadingFontName: Record<PresetHeadingFontKey, string> = {
  Inter: "Inter",
  Newsreader: "Newsreader",
  Montserrat: "Montserrat",
  "General Sans": "General Sans",
  Pally: "Pally",
  "ProximaNova-Regular": "ProximaNova"
}

const schema = z.object({
  primary_color: z.string().optional(),
  heading_font: z.preprocess(
    // Convert empty string to undefined to satisfy zod schema.
    (v) => (v === "" ? undefined : v),
    z
      .union([
        z.literal("Inter"),
        z.literal("Newsreader"),
        z.literal("Montserrat"),
        z.literal("General Sans"),
        z.literal("Pally"),
        z.literal("ProximaNova-Regular"),
        z.undefined(),
        z.null()
      ])
      .optional() satisfies z.Schema<ValidHeadingFont>
  )
})

const validate = toFormikValidate(schema)

type Schema = z.infer<typeof schema>

type ManageBrandProps = {
  card: any
}

const ManageBrand = ({ card }: ManageBrandProps) => {
  const { school } = usePathwrightContext()
  const initialValues = {
    primary_color: school.theme?.primary_color
      ? `#${school.theme?.primary_color}`
      : `#${presetThemes.friendly.primary_color}`,
    heading_font: (school.theme?.heading_font ?? undefined) as ValidHeadingFont
  }

  const [createTheme] = useCreateSchoolThemeMutation()
  const [updateTheme] = useUpdateSchoolThemeMutation()
  // TODO: handle css/links
  const handleSubmit = (values: Schema) => {
    if (values.primary_color) {
      values.primary_color = values.primary_color.replace("#", "")
    }

    const prevPresetHeadingFontCss: string | null = initialValues.heading_font
      ? presetHeadingFontCss[initialValues.heading_font]
      : null
    const nextPresetHeadingFontCss: string | null = values.heading_font
      ? presetHeadingFontCss[values.heading_font]
      : null

    let customCss = school.theme?.custom_css || ""

    if (prevPresetHeadingFontCss !== nextPresetHeadingFontCss) {
      // Parse out previous font css.
      if (prevPresetHeadingFontCss) {
        customCss = parsePresetHeadingFontCss(
          initialValues.heading_font as PresetHeadingFontKey,
          customCss
        )
      }
      // Insert new font css.
      if (nextPresetHeadingFontCss) {
        customCss += `\n${formatPresetHeadingFontCss(
          values.heading_font as PresetHeadingFontKey
        )}`
      }
    }

    const themeInput: ThemeInput = {
      ...values,
      custom_css: customCss
    }

    // Defaulting to using the legacy theme.
    themeInput.theme_type = school.theme
      ? school.theme.theme_type
      : ThemeType.Legacy

    const optimisticTheme: ThemeFragment = {
      id: Math.random() * -1000000,
      ...themeInput
    }

    // After create/update mutations, we need to propagate the created/updated theme to the school.theme field.
    function updateCache(
      cache: ApolloCache<any>,
      theme?: ThemeFragment | null
    ) {
      if (!theme) return

      cache.modify({
        id: cache.identify({
          __typename: "School",
          id: school.id
        }),
        fields: {
          theme: () => theme
        }
      })
    }

    return Promise.all([
      school.theme
        ? updateTheme({
            variables: {
              id: school.theme.id!,
              theme: themeInput
            },
            optimisticResponse: {
              updateSchoolTheme: optimisticTheme
            },
            update: (cache, { data }) => {
              updateCache(cache, data?.updateSchoolTheme)
            }
          })
        : createTheme({
            variables: {
              theme: themeInput
            },
            optimisticResponse: {
              createSchoolTheme: optimisticTheme
            },
            update: (cache, { data }) => {
              updateCache(cache, data?.createSchoolTheme)
            }
          })
    ])
  }

  return (
    <Card card={card} title="Branding">
      <BrandPreview cardPath={card.path} />
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validate}
        validateOnMount
      >
        {(form) => {
          return (
            <Form>
              {!!form.values.heading_font &&
                form.values.heading_font !== initialValues.heading_font && (
                  <PresetThemeFont headingFont={form.values.heading_font} />
                )}
              <CardBlock>
                <Field
                  name="heading_font"
                  as={(props: any) => (
                    <StyledFieldWrapper
                      label="Heading Type Face"
                      helperText="Select the default font to use for headings."
                      labelWidth={180}
                      style={{ width: "100%" }}
                    >
                      <NativeSelect.Root
                        w="160px"
                        ml="auto"
                        // Simply remove from layout to avoid pushing helper text down.
                        pos="absolute"
                        top={0}
                        right={0}
                      >
                        <NativeSelect.Field
                          placeholder={
                            !initialValues.heading_font ? "Select font" : ""
                          }
                          {...props}
                        >
                          {Object.keys(presetHeadingFontCss).map((fontName) => (
                            <option key={fontName} value={fontName}>
                              {
                                PrettyHeadingFontName[
                                  fontName as PresetHeadingFontKey
                                ]
                              }
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator>
                          <Pathicon icon="chevron-down" />
                        </NativeSelect.Indicator>
                      </NativeSelect.Root>
                    </StyledFieldWrapper>
                  )}
                />
              </CardBlock>
              <CardBlock borderTop borderBottom>
                <Field name="primary_color">
                  {({ field }) => (
                    <FieldWrapper
                      label="Primary color"
                      helperText="Your primary color shows up on buttons, links, and accents."
                      labelWidth={180}
                      alignRight
                      style={{ width: "100%" }}
                    >
                      <ColorPicker
                        color={field.value}
                        outputFormat="hex"
                        onChange={(value: string) => {
                          form.setFieldValue("primary_color", value, true)
                        }}
                      />
                    </FieldWrapper>
                  )}
                </Field>
              </CardBlock>
              <SubmitButton
                styleType="primary"
                color={initialValues.primary_color}
                submitting={form.isSubmitting}
                disabled={!form.isValid || !form.dirty}
              >
                Save
              </SubmitButton>
            </Form>
          )
        }}
      </Formik>
      <CardLink
        icon="settings-horizontal"
        label="Add custom CSS (advanced)"
        to={`${card.path}css/`}
      />
    </Card>
  )
}

ManageBrand.displayName = "ManageBrand"

ManageBrand.propTypes = {
  // provided by CardStack > CardStackCard
  card: PropTypes.object.isRequired,
  // handler to save field values
  onSubmit: PropTypes.func.isRequired
}

export default ManageBrand
